import React, { useEffect, useMemo, useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add, DeleteOutline } from '@mui/icons-material';
import type { MealFoodAmount } from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

type MealContentDraft = {
  title: string;
  foodItems: MealFoodAmount[];
};

interface MealContentEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (draft: MealContentDraft) => Promise<void>;
  isSaving: boolean;
  initialDraft?: MealContentDraft | null;
  classNamePrefix: string;
}

type EditableMealItem = {
  name: string;
  amount: string;
  unit: string;
};

const EMPTY_ITEM: EditableMealItem = { name: '', amount: '1', unit: '' };

const toEditableItem = (item: MealFoodAmount): EditableMealItem => ({
  name: item?.name ?? '',
  amount:
    typeof item?.amount === 'number' && Number.isFinite(item.amount) && item.amount > 0
      ? String(item.amount)
      : '1',
  unit:
    typeof (item as { unit?: string }).unit === 'string' ? (item as { unit?: string }).unit! : '',
});

const MealContentEditor: React.FC<MealContentEditorProps> = ({
  open,
  onClose,
  onSave,
  isSaving,
  initialDraft,
  classNamePrefix,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<EditableMealItem[]>([EMPTY_ITEM]);

  useEffect(() => {
    if (!open) return;
    setItems(
      initialDraft?.foodItems?.length
        ? initialDraft.foodItems.map(toEditableItem)
        : [{ ...EMPTY_ITEM }]
    );
  }, [open, initialDraft]);

  const normalizedItems = useMemo(
    () =>
      items
        .map((item) => {
          const trimmedName = item.name.trim();
          const parsedAmount = Number.parseFloat(item.amount);
          if (!trimmedName || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
            return null;
          }
          const unit = item.unit.trim() || undefined;
          return {
            name: trimmedName,
            amount: parsedAmount,
            ...(unit && { unit }),
          } as MealFoodAmount;
        })
        .filter(Boolean) as MealFoodAmount[],
    [items]
  );

  const canSave = normalizedItems.length > 0;

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    await onSave({
      title: normalizedItems[0].name,
      foodItems: normalizedItems,
    });
  };

  if (!open) return;

  return (
    <div className={`${classNamePrefix}__meal-content-editor`}>
      <div className={`${classNamePrefix}__meal-content-items`}>
        {items.map((item, index) => (
          <div
            key={`meal-content-item-${index}`}
            className={`${classNamePrefix}__meal-content-item-row`}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={t('task.richContext.mealEditor.foodName')}
              value={item.name}
              onChange={(e) => {
                const next = [...items];
                next[index] = { ...next[index], name: e.target.value };
                setItems(next);
              }}
              className={`${classNamePrefix}__meal-content-input`}
            />
            <TextField
              size="small"
              type="number"
              placeholder={t('task.richContext.mealEditor.qty')}
              value={item.amount}
              onChange={(e) => {
                const next = [...items];
                next[index] = { ...next[index], amount: e.target.value };
                setItems(next);
              }}
              className={`${classNamePrefix}__meal-content-amount-input`}
              inputProps={{ min: 0, step: 'any' }}
            />
            <TextField
              size="small"
              placeholder={t('task.richContext.mealEditor.unit')}
              value={item.unit}
              onChange={(e) => {
                const next = [...items];
                next[index] = { ...next[index], unit: e.target.value };
                setItems(next);
              }}
              className={`${classNamePrefix}__meal-content-unit-input`}
            />
            {items.length > 1 && (
              <IconButton
                size="small"
                className={`${classNamePrefix}__meal-content-remove-item-btn`}
                aria-label={t('task.richContext.mealEditor.removeMealItem')}
                onClick={() =>
                  setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
                }
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            )}
          </div>
        ))}
      </div>
      <Button
        size="small"
        variant="text"
        startIcon={<Add fontSize="small" />}
        className={`${classNamePrefix}__meal-content-add-item-btn`}
        onClick={() => setItems((prev) => [...prev, { ...EMPTY_ITEM }])}
      >
        {t('task.richContext.mealEditor.addItem')}
      </Button>
      <div className={`${classNamePrefix}__meal-content-editor-actions`}>
        <LoadingButton
          size="small"
          variant="contained"
          loading={isSaving}
          disabled={!canSave}
          onClick={handleSave}
          className={`${classNamePrefix}__meal-content-save-btn`}
        >
          {t('task.common.save')}
        </LoadingButton>
        <Button
          size="small"
          variant="text"
          onClick={onClose}
          className={`${classNamePrefix}__meal-content-cancel-btn`}
        >
          {t('task.common.cancel')}
        </Button>
      </div>
    </div>
  );
};

export type { MealContentDraft };
export default MealContentEditor;
